import { ReloadOutlined } from '@ant-design/icons';
import {
  getAnnDetail,
  getFirstNoticeStatus,
  noticeRecall
} from '@api/announcement';
import ZeissButton from '@components/button';
import RevertModal from '@components/revertModal';
import { ROLE_KEYS_MAP } from '@constants';
import { DicListHook } from '@hooks/useDicListHook';
import { UseSystemCity } from '@hooks/useSystemCity';
import { userInfo } from '@store';
import { Modal, Space } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { useImmer } from 'use-immer';
import { useTimeLine } from '../../../components/useTimeLine/useTimeLine';
import { DataFlagEnum, NOTICE_STATUS } from '../AnnouncementList/announcement';
import { useBaseInfo } from './hooks/AnnouncementDetail/BaseInfo/useBaseInfo';
import { useInfoHead } from './hooks/AnnouncementDetail/BaseInfo/useInfoHead';
import { FirstNoticePopup } from './hooks/AnnouncementDetail/FirstNoticePopup/FirstNoticePopup';
import { useForwardAnnouncement } from './hooks/AnnouncementDetail/ForwardAnnouncement/useForwardAnnouncement';
import { useProjectTable } from './hooks/AnnouncementDetail/Project/useProjectTable';
import { useRelatedAnnouncement } from './hooks/AnnouncementDetail/RelatedAnnouncement/useRelatedAnnouncement';
import { useSubjectMatter } from './hooks/AnnouncementDetail/SubjectMatter/useSubjectMatter';
import { useFooter } from './hooks/Footer/useFooter';
import './index.scss';
import {
  AnnouncementDetailDataInterface,
  NOTICE_REVERT_ARR,
  NOTICE_SUCCESS_ARR
} from './type';

const AnnouncementDetails = () => {
  const [details, setDetails] = useImmer<AnnouncementDetailDataInterface>(
    {} as AnnouncementDetailDataInterface
  );
  const { data: systemCity } = UseSystemCity();
  const dict = DicListHook();
  const [userInfos] = useRecoilState(userInfo);
  const { infoHeadComponent } = useInfoHead({ details }); //公告标题和类型
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const curNoticeCode = `${searchParams.get('noticeId') || ''}`;
  const navigate = useNavigate();
  const [firstNoticePopupOpen, setFirstNoticePopupOpen] = useState(false);
  const [firstNoticeData, setFirstNoticeData] = useState({});
  const [revertModalShow, setRevertModalShow] = useState<boolean>(false);
  const isSales = userInfos.roleKeys === ROLE_KEYS_MAP.sales;
  // 二期销售
  const isDealerSales =
    userInfos.roleKeys === ROLE_KEYS_MAP.sales &&
    details.dataFlag === DataFlagEnum.dealer;

  const isFinishAction = useMemo(
    () => NOTICE_SUCCESS_ARR.includes(details?.noticeStatus),
    [details]
  );
  // 经销商退回 （状态变成了'待销售确认'，此时经销商不可做任何操作）
  const isReturnAction = useMemo(
    () =>
      userInfos.roleKeys === ROLE_KEYS_MAP.dealer &&
      details?.noticeStatus === NOTICE_STATUS.Sales,
    [details]
  );

  const isRevertAction = useMemo(
    () => NOTICE_REVERT_ARR.includes(details?.noticeStatus),
    [details]
  );

  const isSameCurrentSales = useMemo(
    () => details.currentProcessorUserId === userInfos.userId,
    [details]
  );

  const checkNeedOperation = async () => {
    if (isSales && !isSameCurrentSales) {
      return new Promise((resolve) => {
        Modal.confirm({
          title: '提示',
          content: '该公告目前已转给其他销售，请确认是否要继续操作',
          cancelText: '取消',
          okText: '确定',
          onOk: () => {
            resolve(true);
          },
          onCancel: () => {
            resolve(false);
          }
        });
      });
    }

    return true;
  };

  const checkFirstNotice = async (fn?: () => void) => {
    const res =
      (await getFirstNoticeStatus({
        noticeCode: curNoticeCode
      })) ?? {};

    setFirstNoticeData(res);
    const { noticeCode, noticeStatus } = res;
    if (
      noticeCode !== curNoticeCode &&
      !NOTICE_SUCCESS_ARR.includes(noticeStatus)
    ) {
      handleFirstNoticePopupClick();
      return;
    }
    fn?.();
  };

  const getAnnInfo = async () => {
    const res = await getAnnDetail(curNoticeCode);
    await setDetails(
      res
        ? {
            ...res,
            dataFlag: res?.dataFlag || DataFlagEnum.directSelling
          }
        : {}
    );
    await getHistoryList();
    return res;
  };

  const onRevertConfirm = async () => {
    const params = {
      code: curNoticeCode,
      operatorUserId: userInfos.userId,
      operatorUserName: userInfos.realName
    };
    await noticeRecall(params);
    getAnnInfo();
    setRevertModalShow(false);
  };

  const { forwardAnnouncementComponent, confirmHandleAnn, setConfirmOpen } =
    useForwardAnnouncement({
      details,
      checkFirstNotice,
      systemCity,
      dict,
      getAnnInfo,
      checkNeedOperation,
      isFinishAction,
      isReturnAction
    });

  const { baseInfoComponent } = useBaseInfo({
    details,
    dict,
    getAnnInfo,
    isFinishAction,
    isReturnAction,
    systemCity,
    isSameCurrentSales,
    checkNeedOperation
  }); // 公告信息

  const { timeLineComponent, getHistoryList } = useTimeLine({
    details,
    hideForward: true,
    getAnnInfo,
    checkNeedOperation
  }); // 公告信息

  const { relatedAnnouncementComponent } = useRelatedAnnouncement({
    details,
    getAnnInfo,
    isFinishAction,
    isReturnAction,
    checkNeedOperation
  }); // 相关公告

  const { annFooterComponent } = useFooter({
    details,
    noticeCode: details?.noticeCode || '',
    getAnnInfo,
    checkNeedOperation,
    isDealerSales,
    checkFirstNotice
  });
  //标的物
  const { subMatterComponent } = useSubjectMatter({
    details,
    getAnnInfo,
    dict,
    systemCity,
    confirmHandleAnn,
    setConfirmOpen,
    isFinishAction,
    isReturnAction,
    checkNeedOperation
  });
  // 项目
  const { projectTableComponent } = useProjectTable({
    details,
    getAnnInfo,
    dict,
    systemCity,
    isFinishAction,
    isReturnAction,
    checkNeedOperation
  });

  const handleFirstNoticePopupClick = () => {
    setFirstNoticePopupOpen(!firstNoticePopupOpen);
  };

  const handleFirstNoticePopupOk = () => {
    gotoDetailPage(firstNoticeData);
    setFirstNoticePopupOpen(!firstNoticePopupOpen);
  };

  const gotoDetailPage = (data: any) => {
    const { noticeCode } = data;
    navigate(`/announcement/announcementDetail?noticeId=` + noticeCode, {
      replace: true
    });
  };

  useEffect(() => {
    getAnnInfo();
  }, [location]);

  return (
    <div className="pc-annDetail-container">
      <div className="pc-annDetail-layout">
        {infoHeadComponent}
        {relatedAnnouncementComponent}
        <div className="pc-annDetail-tit">
          公告信息
          {details.dataFlag === DataFlagEnum.dealer ? (
            <span className="pc-annDetail-tit-dealer">经销</span>
          ) : (
            <span className="pc-annDetail-tit-sales">直销</span>
          )}
        </div>
        {baseInfoComponent}
        {subMatterComponent}
        {projectTableComponent}

        {(userInfos.roleKeys !== ROLE_KEYS_MAP.sales || isDealerSales) &&
          !isFinishAction &&
          !isReturnAction &&
          annFooterComponent}
        {/* {annFooterComponent} */}
        {!(
          userInfos.roleKeys === ROLE_KEYS_MAP.sales &&
          details.dataFlag === DataFlagEnum.directSelling
        ) &&
          isRevertAction && (
            <Space size={'middle'} className="pc-footer-position">
              <ZeissButton
                type="default"
                className="pc-footer-btn"
                danger
                onClick={() => setRevertModalShow(true)}
              >
                <ReloadOutlined
                  rotate={-30}
                  style={{ transform: 'scaleX(-1)' }}
                />{' '}
                撤回公告
              </ZeissButton>
            </Space>
          )}
        {forwardAnnouncementComponent}
        <FirstNoticePopup
          open={firstNoticePopupOpen}
          handleCancel={handleFirstNoticePopupClick}
          handleOk={handleFirstNoticePopupOk}
        />
      </div>
      <div className="pc-annDetail-history">{timeLineComponent}</div>
      <RevertModal
        title="请确认是否撤回该公告?"
        open={revertModalShow}
        onCancel={() => setRevertModalShow(false)}
        onConfirm={onRevertConfirm}
      />
    </div>
  );
};
export default AnnouncementDetails;
